/**
 * « Refaire » (D-174): what the server allows, said before the click (`retake` of the row) and
 * enforced by the server. A non-subscriber sees the plans; a sitting of the same activity already
 * open is resumed, never mistaken for a new one. The correction under it never changes.
 */

import { ApiError, academyHistoryAttemptsRetakeCreate, academyHistoryLessonsRetakeCreate, getAcademyHistoryRetrieveQueryKey } from "@agencyos/api-client";
import type { HistoryItem } from "@agencyos/api-client";
import { Button, buttonClass } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarPlus, RotateCcw, Sparkles } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

export function RetakeButton({ item, compact = false }: { item: HistoryItem; compact?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [plans, setPlans] = useState(false);
  const size = compact ? "sm" : "md";

  if (item.retake === "none") return null;
  if (item.retake === "oral") {
    return (
      <Link className={buttonClass("secondary", { size })} to="/academy/oral">
        <CalendarPlus aria-hidden size={15} /> {t("academyHistory.retake.oral")}
      </Link>
    );
  }
  if (item.retake === "plans" || plans) {
    return (
      <span className="ac-history__retake">
        {!compact && <span className="ac-footnote">{t("academyHistory.retake.plansNote")}</span>}
        <Link className={buttonClass("tinted", { size })} to="/academy/plans">
          <Sparkles aria-hidden size={15} /> {t("academyHistory.retake.plans")}
        </Link>
      </span>
    );
  }

  const go = async () => {
    setBusy(true);
    setError("");
    try {
      if (item.source === "attempt") {
        const done = await academyHistoryAttemptsRetakeCreate(item.id);
        void queryClient.invalidateQueries({ queryKey: getAcademyHistoryRetrieveQueryKey().slice(0, 1) });
        navigate(`/academy/attempt/${done.data.attempt.id}`, { state: { resumed: done.data.resumed } });
      } else {
        const done = await academyHistoryLessonsRetakeCreate(item.id, { exam: item.exam as never });
        void queryClient.invalidateQueries({ queryKey: getAcademyHistoryRetrieveQueryKey().slice(0, 1) });
        navigate(`/academy/course/lesson/${done.data.lesson}${done.data.exam ? `?exam=${done.data.exam}` : ""}`);
      }
    } catch (reason) {
      if (reason instanceof ApiError && reason.status === 403) setPlans(true);
      else setError(reason instanceof ApiError && reason.detail ? reason.detail : t("academyHistory.retake.failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="ac-history__retake">
      {!compact && <span className="ac-footnote">{t("academyHistory.retake.keeps")}</span>}
      <Button
        variant={compact ? "secondary" : "primary"}
        size={size}
        icon={<RotateCcw aria-hidden size={15} />}
        disabled={busy}
        onClick={() => void go()}
      >
        {busy ? t("academyHistory.retake.starting") : t("academyHistory.retake.action")}
      </Button>
      {error && (
        <span className="ko ac-footnote" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}
